import { FC, useCallback, useMemo } from 'react';
import { WarningAmber } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DataTableColumn, PaginatedProps, StandardListViewV2 } from '../../Components/CoreLib/library';
import { triaxReferenceNumberColumnKeySuffixes } from '../../constants/triaxReferenceNumberColumnKeySuffixes';
import { OrderDto } from '../../dtos';
import {
    useArchiveOrderBulkDeleteMutation,
    useGetOrderQuery,
    useOrderBulkArchiveMutation,
    useOrderBulkUnarchiveMutation,
} from '../../store/generated/generatedApi';
import { format } from 'date-fns';

export interface IOrderReviewOrdersListViewProps {
    date: Date;
}

const defaultPaginationProps: PaginatedProps = {
    sorting: [{ key: 'CUSTOMER', direction: 'asc' }],
    page: 0,
    pageSize: 25,
};

export const OrderReviewOrdersListView: FC<IOrderReviewOrdersListViewProps> = ({ date }) => {
    const navigate = useNavigate();

    const tableColumns: DataTableColumn<OrderDto>[] = useMemo(() => [
        { key: 'projectName', label: 'Project Name', sortKey: 'PROJECT_NAME' },
        { key: 'jobName', label: 'Job Name', sortKey: 'JOB_NAME' },
        { key: 'customerName', label: 'Customer', sortKey: 'CUSTOMER' },
        { key: 'customerNumber', label: 'Customer #', sortKey: 'CUSTOMER_NUMBER' },
        { key: 'quote.addressLine1', label: 'Address', sortKey: 'ADDRESS' },
        { key: 'quote.city', label: 'City', sortKey: 'CITY' },
        { key: 'quote.state', label: 'State', sortKey: 'STATE' },
        { key: 'quote.zipCode', label: 'Zip', sortKey: 'ZIP_CODE' },
        { key: 'quote.quoteNumber', label: 'Quote #', sortKey: 'QUOTE_NUMBER' },
        { key: 'jobNumber', label: 'Job #', sortKey: 'JOB_NUMBER' },
        { key: 'poNumber', label: 'PO #', sortKey: 'PO_NUMBER' },
        {
            key: 'orderNumber',
            label: 'Order #',
            sortKey: 'ORDER_NUMBER',
            keyFieldFormatter: (value: number | undefined) => (value == null ? '' : String(value)),
        },
        { key: 'orderLineItemCount', label: 'Line Items Count', sortKey: 'LINE_ITEM_COUNT' },
        {
            key: 'missingBillingDispatchCount',
            label: 'Billing Validation',
            sortKey: 'LINE_ITEM_COUNT',
            unsorted: true,
            align: 'center',
            fieldMapper: (row) => {
                const missingCount = row.missingBillingDispatchCount ?? 0;
                if (missingCount <= 0) {
                    return '';
                }

                const message = `${missingCount} dispatches missing billing`;
                return (
                    <Tooltip title={message} arrow>
                        <WarningAmber color='warning' />
                    </Tooltip>
                );
            },
        }
    ], []);

    const handleTableEdit = useCallback((id: string, record: OrderDto) => {
        navigate(`/job/${record.jobId}/order/${id}/orderReview/create`);
    }, [navigate]);

    return (
        <StandardListViewV2
            referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
            enableMultiSort
            headerIcon={<></>}
            headerTitle=''
            breadCrumbs={[]}
            defaultPaginationProps={defaultPaginationProps}
            getDataQuery={useGetOrderQuery}
            isShowActiveToggleVisible
            inactivateMutation={useOrderBulkArchiveMutation}
            activateMutation={useOrderBulkUnarchiveMutation}
            deleteMutation={useArchiveOrderBulkDeleteMutation}
            tableColumns={tableColumns}
            entityNameSingular='Order'
            permissionName='review'
            handleEdit={handleTableEdit}
            additionalQueryParameters={{ inReview: false, orderDate: format(date, "yyyy-MM-dd") }}
            paginationOnTop={true}
            stickyTableHeader={true}
        />
    );
};
